import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Descriptions,
  Tag,
  message,
  Space,
  Avatar,
  Spin,
  Typography,
  Empty,
  Alert,
  ConfigProvider,
  Modal,
  Input,
  Tabs,
} from 'antd';
import {
  ArrowRightOutlined,
  PhoneOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  RobotOutlined,
  SoundOutlined,
  UserOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  DollarOutlined,
  UserAddOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  AudioOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  ApiOutlined,
  EditOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { callCenterCallsApi } from '../../utils/api';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { getImagePath } from '../../utils/imageHelper';
import { DetailPageSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useCallCenterOptional } from '../../contexts/CallCenterContext';
import { toJalali } from '../../utils/dateUtils';
import CallAiSummaryPanel, {
  AI_STATUS_META,
  canSummarizeCall,
  hasAnalysisReport,
  isAiInProgress,
  normalizeAiStatus,
} from '../../components/call-center/CallAiSummaryPanel';
import CallTranscriptView from '../../components/call-center/CallTranscriptView';
import DetailSection from '../../components/call-center/DetailSection';
import CallSubjectChip from '../../components/call-center/subjects/CallSubjectChip';
import CallSubjectSelect from '../../components/call-center/subjects/CallSubjectSelect';
import CallInsightsSection from '../../components/call-center/CallInsightsSection';

const { Text } = Typography;


const DIRECTION_META = {
  incoming: { label: 'ورودی', color: 'blue', icon: <ArrowDownOutlined /> },
  outgoing: { label: 'خروجی', color: 'green', icon: <ArrowUpOutlined /> },
};

const DISPOSITION_META = {
  ANSWERED: { label: 'پاسخ داده', color: 'green' },
  'NO ANSWER': { label: 'بی‌پاسخ', color: 'orange' },
  NOANSWER: { label: 'بی‌پاسخ', color: 'orange' },
  BUSY: { label: 'مشغول', color: 'gold' },
  FAILED: { label: 'ناموفق', color: 'red' },
};

const TYPE_META = {
  client: { label: 'مشتری', color: 'green', icon: <TeamOutlined />, path: (id) => `/clients/${id}` },
  lead: { label: 'سرنخ', color: 'orange', icon: <UserAddOutlined />, path: (id) => `/leads/${id}` },
  deal: { label: 'معامله', color: 'blue', icon: <DollarOutlined />, path: (id) => `/deals/${id}` },
};

const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds) || 0;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

/** کارت ریز برای نمایش سایر رکوردهای منطبق با کلیک برای جابجایی */
const MatchChip = ({ item, type, onNavigate }) => {
  const meta = TYPE_META[type] || TYPE_META.client;
  const name = item.name || item.client_name || '—';
  const sub =
    item.mobile ||
    item.phone ||
    item.contact?.mobile ||
    item.contact?.client_name ||
    item.client?.name ||
    item.email ||
    item.cell ||
    '';

  return (
    <div
      onClick={() => {
        if (item.id) onNavigate(meta.path(item.id));
      }}
      className="cursor-pointer flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-teal-400 dark:hover:border-teal-500 hover:shadow-sm transition-all group"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: type === 'client' ? 'rgba(16,185,129,0.12)' : type === 'lead' ? 'rgba(249,115,22,0.12)' : 'rgba(59,130,246,0.12)',
            color: type === 'client' ? '#059669' : type === 'lead' ? '#ea580c' : '#2563eb',
          }}
        >
          {meta.icon}
        </div>
        <div className="min-w-0">
          <div className="custom-font font-medium text-gray-800 dark:text-gray-100 text-sm truncate">{name}</div>
          {sub && (
            <div className="custom-font text-xs text-gray-500 truncate" dir="ltr">
              {sub}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Tag color={meta.color} className="custom-font m-0 text-xs">
          {meta.label}
        </Tag>
        <ArrowLeftOutlined className="text-gray-300 group-hover:text-teal-500 transition-colors text-xs" />
      </div>
    </div>
  );
};

const ViewCallCenterCall = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const callCenter = useCallCenterOptional();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = ['overview', 'analysis', 'conversation'].includes(requestedTab) ? requestedTab : 'overview';

  // تب فعال در آدرس نگه داشته می‌شود تا با رفرش یا اشتراک لینک همان تب باز شود
  const handleTabChange = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'overview') next.delete('tab');
      else next.set('tab', key);
      return next;
    }, { replace: true });
  };

  const goBackToList = () => {
    navigate(returnToRef.current || '/call-center/calls', { state: buildGoBackState() });
  };

  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const pollIntervalRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const aiAlertedRef = useRef(false);
  const transcribeAlertedRef = useRef(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [editingReportText, setEditingReportText] = useState('');
  const [savingReport, setSavingReport] = useState(false);
  const [savingSubject, setSavingSubject] = useState(false);
  const [savingObjections, setSavingObjections] = useState(false);
  const [reviewingRisk, setReviewingRisk] = useState(false);
  const [reviewingMissed, setReviewingMissed] = useState(false);

  // اصلاح دستی اعتراض‌ها؛ در صورت خطا فهرست قبلی برمی‌گردد
  const handleObjectionsChange = async (ids) => {
    if (!call?.id) return;
    const previous = call.objections || [];
    setSavingObjections(true);
    try {
      const res = await callCenterCallsApi.update(call.id, { objection_ids: ids });
      setCall((prev) => (prev ? { ...prev, objections: res?.data?.objections ?? previous } : prev));
      message.success('اعتراض‌های تماس ثبت شد');
    } catch (err) {
      setCall((prev) => (prev ? { ...prev, objections: previous } : prev));
      message.error(err?.message || 'خطا در ثبت اعتراض‌ها');
    } finally {
      setSavingObjections(false);
    }
  };

  // تأیید بررسی تماس پرریسک توسط مدیر
  const handleRiskReviewed = async () => {
    if (!call?.id) return;
    setReviewingRisk(true);
    try {
      await callCenterCallsApi.update(call.id, { risk_reviewed: true });
      setCall((prev) => (prev ? { ...prev, risk_reviewed_at: new Date().toISOString() } : prev));
      message.success('تماس به عنوان بررسی‌شده ثبت شد');
    } catch (err) {
      message.error(err?.message || 'خطا در ثبت بررسی تماس');
    } finally {
      setReviewingRisk(false);
    }
  };

  // تأیید پیگیری فرصت فروش ازدست‌رفته توسط مدیر
  const handleMissedReviewed = async () => {
    if (!call?.id) return;
    setReviewingMissed(true);
    try {
      await callCenterCallsApi.update(call.id, { missed_reviewed: true });
      setCall((prev) => (prev ? { ...prev, missed_opportunity_reviewed_at: new Date().toISOString() } : prev));
      message.success('فرصت فروش به عنوان پیگیری‌شده ثبت شد');
    } catch (err) {
      message.error(err?.message || 'خطا در ثبت پیگیری');
    } finally {
      setReviewingMissed(false);
    }
  };

  // تغییر خوش‌بینانه موضوع تماس؛ در صورت خطا به مقدار قبلی برمی‌گردد
  const handleSubjectChange = async (subjectId, subject) => {
    if (!call?.id) return;
    const previous = { subject_id: call.subject_id ?? null, subject: call.subject ?? null };
    setCall((prev) => (prev ? { ...prev, subject_id: subjectId, subject } : prev));
    setSavingSubject(true);
    try {
      await callCenterCallsApi.update(call.id, { subject_id: subjectId });
      message.success(subjectId ? 'موضوع تماس ثبت شد' : 'موضوع تماس حذف شد');
    } catch (err) {
      setCall((prev) => (prev ? { ...prev, ...previous } : prev));
      message.error(err?.message || 'خطا در ثبت موضوع تماس');
    } finally {
      setSavingSubject(false);
    }
  };

  const handleSaveReport = async () => {
    if (!call?.id) return;
    setSavingReport(true);
    try {
      const trimmed = editingReportText.trim();
      await callCenterCallsApi.update(call.id, { report: trimmed });
      setCall((prev) => (prev ? { ...prev, report: trimmed } : prev));
      message.success('گزارش تماس با موفقیت ذخیره شد');
      setReportModalOpen(false);
    } catch (err) {
      console.error(err);
      message.error('خطا در ذخیره گزارش تماس');
    } finally {
      setSavingReport(false);
    }
  };

  const stopAiPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const loadAudio = useCallback(async (callId) => {
    setAudioLoading(true);
    try {
      const res = await callCenterCallsApi.getAudioLink(callId);
      const link = res?.data?.audio_link || res?.data?.audio_url || res?.audio_link || res?.audio_url;
      if (link) {
        setAudioUrl(link);
      } else {
        setAudioUrl(null);
        message.warning('لینک فایل صوتی در دسترس نیست');
      }
    } catch (error) {
      console.error('خطا در دریافت فایل صوتی:', error);
      setAudioUrl(null);
      message.error(error?.message || 'خطا در دریافت فایل صوتی');
    } finally {
      setAudioLoading(false);
    }
  }, []);

  const applyCallData = useCallback((data) => {
    setCall(data);
    // Prioritize exact audio_link over generic endpoint audio_url
    const exactLink = data?.audio_link || (data?.audio_url && !data.audio_url.includes('/calls/') ? data.audio_url : null);
    if (exactLink) {
      setAudioUrl(exactLink);
    } else {
      setAudioUrl(null);
    }
    return normalizeAiStatus(data?.ai_status);
  }, []);

  const fetchCallStatus = useCallback(async ({ silent = false, showRefresh = false } = {}) => {
    if (showRefresh) setRefreshing(true);
    else if (!silent) setLoading(true);
    try {
      const res = await callCenterCallsApi.getById(id);
      const data = res?.data ?? null;
      const status = applyCallData(data);
      if (!data?.audio_link && (data?.has_audio !== false && data?.audio_file)) {
        loadAudio(id);
      }
      return { data, status };
    } catch (error) {
      if (!silent) {
        console.error('خطا در دریافت جزئیات تماس:', error);
        message.error(error?.message || 'خطا در دریافت جزئیات تماس');
      }
      return { data: null, status: null };
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id, applyCallData, loadAudio]);

  const handlePlayAudio = async () => {
    await loadAudio(id);
  };

  const pollAiStatusOnce = useCallback(async () => {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const res = await callCenterCallsApi.getProcessingStatus([Number(id)]);
      const update = Array.isArray(res?.data) ? res.data[0] : null;
      if (!update) return;

      setCall((prev) => (prev ? { ...prev, ...update } : prev));

      // متن کامل سگمنت‌ها فقط در جزئیات تماس است؛ پس از پایان رونویسی یک‌بار کامل دریافت می‌شود
      if (update.transcribe_status === 'completed' && !transcribeAlertedRef.current) {
        await fetchCallStatus({ silent: true });
      }

      const aiStatus = normalizeAiStatus(update.ai_status);
      const tStatus = update.transcribe_status;

      if (aiStatus === 'completed' && !aiAlertedRef.current) {
        aiAlertedRef.current = true;
        message.success('خلاصه مکالمه با موفقیت تولید شد');
      } else if (aiStatus === 'failed' && !aiAlertedRef.current) {
        aiAlertedRef.current = true;
        message.error('خلاصه‌سازی مکالمه با خطا مواجه شد');
      }

      if (tStatus === 'completed' && !transcribeAlertedRef.current) {
        transcribeAlertedRef.current = true;
        message.success('رونویسی مکالمه با موفقیت انجام شد');
      } else if (tStatus === 'failed' && !transcribeAlertedRef.current) {
        transcribeAlertedRef.current = true;
        message.error('رونویسی مکالمه با خطا مواجه شد');
      }

      if (!isAiInProgress(aiStatus) && tStatus !== 'pending' && tStatus !== 'processing') {
        stopAiPolling();
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, [id, fetchCallStatus, stopAiPolling]);

  const startAiPolling = useCallback(() => {
    stopAiPolling();
    pollIntervalRef.current = setInterval(() => {
      if (!document.hidden) pollAiStatusOnce();
    }, 5000); // وضعیت رونویسی و خلاصه با endpoint سبک processing-status
  }, [pollAiStatusOnce, stopAiPolling]);

  useEffect(() => {
    let cancelled = false;
    setAudioUrl(null);
    (async () => {
      const { status: aiStatus, data } = await fetchCallStatus();
      if (cancelled) return;
      const tStatus = data?.transcribe_status;
      aiAlertedRef.current = !isAiInProgress(aiStatus);
      transcribeAlertedRef.current = (tStatus !== 'pending' && tStatus !== 'processing');
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    })();
    return () => {
      cancelled = true;
      stopAiPolling();
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const res = await callCenterCallsApi.summarize(id);
      message.success(res?.message || 'درخواست خلاصه‌سازی در صف قرار گرفت');
      aiAlertedRef.current = false;
      const { status: aiStatus, data } = await fetchCallStatus({ silent: true, showRefresh: true });
      const tStatus = data?.transcribe_status;
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    } catch (error) {
      console.error('خطا در خلاصه‌سازی:', error);
      message.error(error?.message || 'خطا در درخواست خلاصه‌سازی');
    } finally {
      setSummarizing(false);
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      const res = await callCenterCallsApi.transcribe(id);
      message.success(res?.message || 'درخواست تبدیل صوت به متن در صف پردازش قرار گرفت.');
      transcribeAlertedRef.current = false;
      const { status: aiStatus, data } = await fetchCallStatus({ silent: true, showRefresh: true });
      const tStatus = data?.transcribe_status;
      if (isAiInProgress(aiStatus) || tStatus === 'pending' || tStatus === 'processing') {
        startAiPolling();
      }
    } catch (error) {
      console.error('خطا در رونویسی:', error);
      message.error(error?.message || 'خطا در درخواست رونویسی');
    } finally {
      setTranscribing(false);
    }
  };

  // اطلاعات تطبیقی برگشتی از API
  const match = useMemo(() => call?.match || {}, [call?.match]);
  const primary = match?.primary || null;
  const primaryMeta = primary?.type ? TYPE_META[primary.type] : null;

  const otherMatches = useMemo(() => {
    const items = [];
    (match.clients || []).forEach((c) => {
      if (!(primary?.type === 'client' && Number(primary?.id) === Number(c.id))) {
        items.push({ item: c, type: 'client' });
      }
    });
    (match.leads || []).forEach((l) => {
      if (!(primary?.type === 'lead' && Number(primary?.id) === Number(l.id))) {
        items.push({ item: l, type: 'lead' });
      }
    });
    (match.deals || []).forEach((d) => {
      if (!(primary?.type === 'deal' && Number(primary?.id) === Number(d.id))) {
        items.push({ item: d, type: 'deal' });
      }
    });
    return items;
  }, [match, primary]);

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font" style={{ fontFamily: 'CustomFont, sans-serif' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (!call) {
    return (
      <MainLayout>
        <ConfigProvider theme={{ token: { fontFamily: 'CustomFont, sans-serif' } }}>
          <Card className="shadow-xl border-0 custom-font">
            <Empty description={<span className="custom-font">تماس یافت نشد</span>} />
            <div className="text-center mt-4">
              <Button className="custom-font" onClick={goBackToList}>
                بازگشت به لیست
              </Button>
            </div>
          </Card>
        </ConfigProvider>
      </MainLayout>
    );
  }

  const dirMeta = DIRECTION_META[call.direction] || { label: call.direction, color: 'default' };
  const dispMeta = DISPOSITION_META[call.disposition] || { label: call.disposition, color: 'default' };
  const aiStatus = normalizeAiStatus(call.ai_status);
  const aiMeta = AI_STATUS_META[aiStatus] || AI_STATUS_META.none;
  const isAiPending = isAiInProgress(aiStatus);
  
  const tStatus = call.transcribe_status || (call.is_transcribed ? 'completed' : 'none');
  const isTranscribePending = tStatus === 'pending';
  const isTranscribeProcessing = tStatus === 'processing';
  const isTranscribeFailed = tStatus === 'failed';
  const isTranscribeCompleted = tStatus === 'completed';

  const canSummarize = canSummarizeCall(call) && !isAiPending;
  const showAiSection = isTranscribeCompleted || Boolean(call.summary) || aiStatus !== 'none';
  const showTranscript = isTranscribeCompleted || Boolean(call.transcript && !(call.transcribed_segments?.length > 0));
  // بخش تحلیل هوشمند فقط وقتی نمایش داده می‌شود که رونویسی/خلاصه/وضعیتی از AI وجود داشته باشد
  const hasInsightsColumn = showAiSection;

  const minDuration = Number(call?.transcribe_min_duration) || 30;
  const callDuration = Number(call?.duration) || Number(call?.billsec) || 0;
  const isEligibleDuration = call?.can_transcribe !== undefined
    ? Boolean(call.can_transcribe)
    : (callDuration >= minDuration);
  const canTranscribe = call.has_audio !== false
    && (audioUrl || call.audio_url || call.audio_file)
    && (tStatus === 'none' || tStatus === 'failed')
    && isEligibleDuration;

  const customerPhone = call.phone || (call.direction === 'outgoing' ? call.dst : call.src) || '';

  return (
    <MainLayout>
      <ConfigProvider theme={{ token: { fontFamily: 'CustomFont, sans-serif' } }}>
        <div className="custom-font w-full max-w-full overflow-x-hidden" style={{ fontFamily: 'CustomFont, sans-serif', minWidth: 0 }}>
          <Card className="shadow-xl border-0 custom-font w-full max-w-full" bodyStyle={{ padding: 0, fontFamily: 'CustomFont, sans-serif', overflow: 'hidden', maxWidth: '100%' }}>
            {/* نوار عنوان صفحه */}
            <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <Button
                  type="text"
                  icon={<ArrowRightOutlined />}
                  onClick={goBackToList}
                  className="custom-font"
                />
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white flex items-center justify-center shadow-lg">
                  <PhoneOutlined className="text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 custom-font">
                    جزئیات تماس
                  </h1>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    <span className="text-gray-500 dark:text-gray-400 text-sm custom-font" dir="ltr">
                      {customerPhone || '—'}
                    </span>
                    {call.subject && <CallSubjectChip subject={call.subject} size="small" />}
                  </div>
                </div>
              </div>
              <Space wrap>
                <Button
                  icon={<ReloadOutlined />}
                  loading={refreshing}
                  onClick={() => fetchCallStatus({ showRefresh: true })}
                  className="custom-font"
                >
                  بروزرسانی
                </Button>
                {canTranscribe && (
                  <Button
                    icon={<AudioOutlined />}
                    loading={transcribing}
                    onClick={handleTranscribe}
                    className="custom-font text-purple-600 border-purple-500 hover:text-purple-700 hover:border-purple-600"
                  >
                    ارسال برای رونویسی
                  </Button>
                )}
                {isTranscribePending && (
                  <Button disabled icon={<ClockCircleOutlined />} className="custom-font">
                    در صف رونویسی...
                  </Button>
                )}
                {isTranscribeProcessing && (
                  <Button disabled icon={<LoadingOutlined />} className="custom-font text-purple-500 border-purple-300">
                    در حال رونویسی...
                  </Button>
                )}
                <Button
                  icon={<ApiOutlined />}
                  onClick={() => navigate(`/call-center/calls/${id}/transcription-logs`)}
                  className="custom-font text-purple-600 border-purple-400 hover:text-purple-700"
                >
                  لاگ‌های رونویسی
                </Button>
                {canSummarize && (
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    loading={summarizing}
                    onClick={handleSummarize}
                    className="custom-font bg-purple-600 hover:bg-purple-700 text-white border-0 shadow-sm"
                  >
                    {call.summary ? 'تحلیل مجدد با هوش مصنوعی' : 'خلاصه‌سازی با هوش مصنوعی'}
                  </Button>
                )}
              </Space>
            </div>


            {/* بدنه محتوا */}
            <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl space-y-4 custom-font w-full max-w-full overflow-x-hidden" style={{ minWidth: 0 }}>

              {/* هشدار تماس پرریسک: بالاتر از همه بخش‌ها تا مدیر فوراً ببیند */}
              {call.high_risk && (
                <Alert
                  type={call.risk_reviewed_at ? 'warning' : 'error'}
                  showIcon
                  icon={<WarningOutlined />}
                  className="custom-font"
                  message={(
                    <span className="custom-font font-bold">
                      {call.risk_reviewed_at ? 'تماس پرریسک (بررسی شده)' : 'تماس پرریسک — نیازمند بررسی فوری'}
                    </span>
                  )}
                  description={(
                    <ul className="custom-font m-0 ps-4 space-y-0.5">
                      {(call.high_risk_reasons || []).map((reason, index) => (
                        <li key={`${reason.type}-${index}`}>
                          <b>{reason.label}</b>
                          {reason.evidence ? <span className="text-gray-600 dark:text-gray-300">: «{reason.evidence}»</span> : null}
                        </li>
                      ))}
                      {(call.high_risk_reasons || []).length === 0 && <li>دلیل مشخصی ثبت نشده است.</li>}
                    </ul>
                  )}
                  action={!call.risk_reviewed_at && (
                    <Button size="small" danger loading={reviewingRisk} onClick={handleRiskReviewed} className="custom-font">
                      بررسی شد
                    </Button>
                  )}
                />
              )}

              {/* هشدار فرصت فروش ازدست‌رفته */}
              {call.missed_opportunity && (
                <Alert
                  type={call.missed_opportunity_reviewed_at ? 'info' : 'warning'}
                  showIcon
                  className="custom-font"
                  message={(
                    <span className="custom-font font-bold">
                      {call.missed_opportunity_reviewed_at ? 'فرصت فروش ازدست‌رفته (پیگیری شده)' : 'فرصت فروش ازدست‌رفته — نیازمند پیگیری'}
                    </span>
                  )}
                  description={(
                    <div className="custom-font space-y-1">
                      {call.missed_opportunity_details?.summary && <div>{call.missed_opportunity_details.summary}</div>}
                      <ul className="m-0 ps-4 space-y-0.5">
                        {(call.missed_opportunity_details?.signals || []).map((sig, index) => (
                          <li key={`${sig.signal}-${index}`}>
                            <b>{sig.signal_label}</b>
                            {sig.customer_evidence ? <span className="text-gray-600 dark:text-gray-300">: «{sig.customer_evidence}»</span> : null}
                            <span> — {sig.gap_label}</span>
                            {sig.recommended_action ? <div className="text-xs text-gray-500">اقدام پیشنهادی: {sig.recommended_action}</div> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  action={!call.missed_opportunity_reviewed_at && (
                    <Button size="small" loading={reviewingMissed} onClick={handleMissedReviewed} className="custom-font">
                      پیگیری شد
                    </Button>
                  )}
                />
              )}

              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                className="custom-font call-detail-tabs"
                size="large"
                destroyInactiveTabPane={false}
                items={[
                  {
                    key: 'overview',
                    label: <span className="custom-font inline-flex items-center gap-1.5"><UserOutlined />نمای کلی</span>,
                    children: (
                      <div className="space-y-4 min-w-0">
                        {call.summary && (
                          <DetailSection
                            icon={<RobotOutlined />}
                            color="purple"
                            title="خلاصه مکالمه"
                            description="خلاصه تولیدشده با هوش مصنوعی"
                            extra={<Button type="link" size="small" onClick={() => handleTabChange('analysis')} className="custom-font text-xs">مشاهده تحلیل کامل</Button>}
                          >
                            <div className="custom-font text-sm leading-7 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{call.summary}</div>
                          </DetailSection>
                        )}

              {/* بخش ۱: مخاطب تماس در CRM */}
              <DetailSection
                icon={<UserOutlined />}
                title="مخاطب تماس"
                description="مشتری، سرنخ یا معامله‌ای که با این شماره در CRM شناسایی شده است"
                plain
              >
                {primary ? (
                  <div className="rounded-2xl border-2 border-teal-300 dark:border-teal-700 bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-teal-950/20 shadow-sm p-4 transition-all">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <Avatar
                          size={52}
                          className="shrink-0 bg-gradient-to-br from-teal-500 to-emerald-600 text-white font-bold text-2xl shadow-md"
                          src={getImagePath(primary.image)}
                          icon={!primary.name && !primary.image ? <UserOutlined /> : undefined}
                        >
                          {(primary.name || '?').charAt(0)}
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="custom-font text-xl font-bold text-gray-900 dark:text-white">
                              {primary.name || '—'}
                            </span>
                            {primaryMeta && (
                              <Tag color={primaryMeta.color} className="custom-font m-0 font-medium">
                                {primaryMeta.label}
                              </Tag>
                            )}
                            <Tag color="cyan" className="custom-font m-0 text-xs">
                              مخاطب اصلی شناسایی‌شده
                            </Tag>
                          </div>
                          {primary.company_name && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 custom-font mb-1">
                              <BankOutlined className="text-teal-600 dark:text-teal-400" />
                              <span>{primary.company_name}</span>
                            </div>
                          )}
                          {primary.contact_name && (
                            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 custom-font mb-1">
                              <UserOutlined className="text-blue-500" />
                              <span>مخاطب معامله: {primary.contact_name}</span>
                            </div>
                          )}
                          {primary.mobile && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 custom-font" dir="ltr">
                              {primary.mobile}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* دکمه‌های اقدام سریع */}
                      <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        {customerPhone && callCenter?.clickToCall && (
                          <Button
                            icon={<PhoneOutlined />}
                            onClick={() => callCenter.clickToCall(customerPhone)}
                            className="custom-font"
                          >
                            تماس سریع
                          </Button>
                        )}
                        {primaryMeta && primary.id && (
                          <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(primaryMeta.path(primary.id))}
                            className={PANEL_PRIMARY_BTN_CLASS}
                          >
                            مشاهده پروفایل {primaryMeta.label}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* سایر تطبیق‌های یافت‌شده برای این شماره */}
                    {otherMatches.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-teal-100 dark:border-teal-900/60">
                        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 custom-font">
                          سایر تطبیق‌های CRM با این شماره ({otherMatches.length} مورد):
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {otherMatches.map(({ item, type }) => (
                            <MatchChip
                              key={`${type}-${item.id}`}
                              item={item}
                              type={type}
                              onNavigate={navigate}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-5 text-center">
                    <UserOutlined className="text-3xl text-gray-400 mb-2" />
                    <p className="custom-font font-medium text-gray-700 dark:text-gray-200 m-0">
                      مخاطبی در CRM برای این شماره ثبت نشده است
                    </p>
                    <p className="custom-font text-gray-400 m-0 text-xs mt-1" dir="ltr">
                      {customerPhone}
                    </p>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => navigate(`/leads/add?mobile=${encodeURIComponent(customerPhone)}`)}
                        className="custom-font"
                      >
                        افزودن به عنوان سرنخ
                      </Button>
                      <Button
                        size="small"
                        icon={<UserAddOutlined />}
                        onClick={() => navigate(`/clients/create?mobile=${encodeURIComponent(customerPhone)}`)}
                        className="custom-font"
                      >
                        افزودن به عنوان مشتری
                      </Button>
                    </div>
                  </div>
                )}
              </DetailSection>

                  {/* بخش ۲: اطلاعات تماس */}
                  <DetailSection
                    icon={<FileTextOutlined />}
                    color="sky"
                    title="اطلاعات تماس"
                    description="موضوع، وضعیت، زمان‌بندی و نتیجه این تماس"
                  >
                    <Descriptions
                      column={{ xs: 1, sm: 2, md: 3 }}
                      size="small"
                      className="custom-font"
                      labelStyle={{ fontFamily: 'CustomFont, sans-serif' }}
                      contentStyle={{ fontFamily: 'CustomFont, sans-serif' }}
                    >
                      <Descriptions.Item label="موضوع تماس" span="filled">
                        <CallSubjectSelect
                          value={call.subject_id}
                          loading={savingSubject}
                          onChange={handleSubjectChange}
                          placeholder="انتخاب موضوع تماس"
                          style={{ minWidth: 220 }}
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="نوع تماس">
                        <Tag color={dirMeta.color} icon={dirMeta.icon} className="custom-font">
                          {dirMeta.label}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="وضعیت">
                        <Tag color={dispMeta.color} className="custom-font">{dispMeta.label}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="شماره مخاطب">
                        <Text className="custom-font" dir="ltr">{call.phone || '—'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="داخلی پاسخگو">
                        <Text className="custom-font" dir="ltr">{call.extension || '—'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="صف تماس">
                        <Text className="custom-font">
                          {typeof call.queue === 'object' && call.queue !== null
                            ? (call.queue.title && call.queue.title !== call.queue.name
                                ? `${call.queue.title} (${call.queue.name})`
                                : (call.queue.title || call.queue.name || '—'))
                            : (call.queue || '—')}
                        </Text>
                      </Descriptions.Item>
                      {call.outcome && (
                        <Descriptions.Item label="نتیجه تماس">
                          <Tag color="blue" className="custom-font">
                            {typeof call.outcome === 'object' ? (call.outcome.name || '—') : call.outcome}
                          </Tag>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="زمان انتظار">
                        <Text className="custom-font" dir="ltr">{formatDuration(call.wait)}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="مدت مکالمه">
                        <Text className="custom-font" dir="ltr">{formatDuration(call.billsec ?? call.duration)}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="زمان تماس">
                        <Text className="custom-font" dir="ltr">
                          {call.started_at
                            ? toJalali(call.started_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.started_at).toLocaleString('fa-IR')
                            : '—'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="زمان پاسخگویی">
                        <Text className="custom-font" dir="ltr">
                          {call.answered_at
                            ? toJalali(call.answered_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.answered_at).toLocaleString('fa-IR')
                            : '—'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="زمان پایان">
                        <Text className="custom-font" dir="ltr">
                          {call.ended_at
                            ? toJalali(call.ended_at, 'jYYYY/jMM/jDD HH:mm:ss') || new Date(call.ended_at).toLocaleString('fa-IR')
                            : '—'}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="وضعیت AI">
                        <Space size={8}>
                          <Tag color={aiMeta.color} className="custom-font">{aiMeta.label}</Tag>
                          {isAiPending && <Spin size="small" />}
                        </Space>
                      </Descriptions.Item>
                      {call.simotel_uniqueid && (
                        <Descriptions.Item label="شناسه یکتا (UniqueID)">
                          <Text className="custom-font text-xs text-gray-500" dir="ltr">
                            {call.simotel_uniqueid}
                          </Text>
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </DetailSection>

              {/* گزارش / یادداشت تماس: بخش مستقل با عرض کامل تا در ستون باریک جا نشود */}
              <DetailSection
                icon={<EditOutlined />}
                color="sky"
                title="گزارش / یادداشت تماس"
                description="شرح مکالمه، توافقات و نکات مهم ثبت‌شده توسط اپراتور"
                extra={(
                  <Button
                    size="small"
                    type={call.report ? 'default' : 'primary'}
                    icon={<EditOutlined />}
                    className={`custom-font ${call.report ? '' : PANEL_PRIMARY_BTN_CLASS}`}
                    onClick={() => {
                      setEditingReportText(call.report || '');
                      setReportModalOpen(true);
                    }}
                  >
                    {call.report ? 'ویرایش' : 'ثبت گزارش'}
                  </Button>
                )}
              >
                <div className={`custom-font text-sm leading-7 break-words whitespace-pre-wrap min-w-0 ${call.report ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'}`}>
                  {call.report || 'هنوز گزارشی برای این تماس ثبت نشده است.'}
                </div>
              </DetailSection>

                  {/* بخش ۳: طرفین تماس (اپراتور و مخاطب) */}
                  {(call.client || call.lead || call.deal || call.agent) && (
                    <DetailSection
                      icon={<TeamOutlined />}
                      color="slate"
                      title="طرفین مکالمه"
                      description="اپراتور پاسخ‌دهنده و طرف مقابل تماس"
                    >
                      <div className="flex flex-wrap gap-8">
                        {call.agent && (
                          <div className="flex items-center gap-3">
                            <Avatar src={getImagePath(call.agent.image)} size={48} className="bg-teal-600 text-white font-bold">
                              {call.agent.name?.charAt(0)}
                            </Avatar>
                            <div>
                              <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.agent.name}</div>
                              <div className="custom-font text-xs text-gray-500">اپراتور پاسخ‌دهنده (داخلی {call.extension || '—'})</div>
                            </div>
                          </div>
                        )}
                        {call.client && (
                          <div className="flex items-center gap-3">
                            <Avatar src={getImagePath(call.client.image)} size={48} icon={<UserOutlined />} className="bg-emerald-600 text-white" />
                            <div>
                              <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.client.name}</div>
                              <div className="custom-font text-xs text-gray-500">مشتری متصل به تماس</div>
                              {call.client_user_id && (
                                <Button
                                  type="link"
                                  size="small"
                                  className="custom-font p-0 h-auto"
                                  onClick={() => navigate(`/clients/${call.client_user_id}`)}
                                >
                                  مشاهده پروفایل مشتری
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                        {!call.client && call.lead && (
                          <div className="flex items-center gap-3">
                            <Avatar size={48} icon={<UserAddOutlined />} className="bg-orange-600 text-white" />
                            <div>
                              <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.lead.client_name || call.lead.company_name}</div>
                              <div className="custom-font text-xs text-gray-500">سرنخ متصل به تماس</div>
                              {call.lead_id && (
                                <Button
                                  type="link"
                                  size="small"
                                  className="custom-font p-0 h-auto"
                                  onClick={() => navigate(`/leads/${call.lead_id}`)}
                                >
                                  مشاهده پروفایل سرنخ
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                        {!call.client && !call.lead && call.deal && (
                          <div className="flex items-center gap-3">
                            <Avatar size={48} icon={<DollarOutlined />} className="bg-blue-600 text-white" />
                            <div>
                              <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{call.deal.name}</div>
                              <div className="custom-font text-xs text-gray-500">معامله متصل به تماس</div>
                              {call.deal_id && (
                                <Button
                                  type="link"
                                  size="small"
                                  className="custom-font p-0 h-auto"
                                  onClick={() => navigate(`/deals/${call.deal_id}`)}
                                >
                                  مشاهده معامله
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </DetailSection>
                  )}
                      </div>
                    ),
                  },
                  {
                    key: 'analysis',
                    label: (
                      <span className="custom-font inline-flex items-center gap-1.5">
                        <RobotOutlined />
                        ارزیابی و تحلیل AI
                        {call.operator_score != null && (
                          <Tag className="custom-font m-0 text-[11px]" color={call.operator_score >= 75 ? 'green' : call.operator_score >= 60 ? 'gold' : 'red'}>{call.operator_score}</Tag>
                        )}
                        {isAiPending && <Spin size="small" />}
                      </span>
                    ),
                    children: (
                      <div className="space-y-4 min-w-0">
              {/* ارزیابی تماس: امتیاز اپراتور، FCR، اعتراض‌ها و Coaching */}
              {(aiStatus === 'completed' || call.operator_score != null || (call.objections || []).length > 0) && (
                <DetailSection
                  icon={<SafetyCertificateOutlined />}
                  color="amber"
                  title="ارزیابی و تحلیل تماس"
                  description="امتیاز اپراتور، FCR، اعتراض‌ها و توضیح Coaching"
                >
                  <CallInsightsSection
                    call={call}
                    savingObjections={savingObjections}
                    onObjectionsChange={handleObjectionsChange}
                  />
                </DetailSection>
              )}

                    {hasInsightsColumn && (
                    <DetailSection
                      icon={<RobotOutlined />}
                      color="purple"
                      title="تحلیل هوشمند مکالمه"
                      description="خلاصه، نکات کلیدی، مراحل مکالمه و پیشنهادهای بهبود؛ تولیدشده با هوش مصنوعی"
                      plain={hasAnalysisReport(call)}
                    >
                      <CallAiSummaryPanel call={call} summarizing={summarizing} onSummarize={handleSummarize} />
                    </DetailSection>
                    )}

                        {!hasInsightsColumn && !(aiStatus === 'completed' || call.operator_score != null || (call.objections || []).length > 0) && (
                          <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-8 text-center space-y-3">
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description={<span className="custom-font text-gray-500">هنوز تحلیلی برای این تماس وجود ندارد. ابتدا مکالمه باید رونویسی شود، سپس تحلیل با هوش مصنوعی اجرا می‌شود.</span>}
                            />
                            {canSummarize && (
                              <Button type="primary" icon={<RobotOutlined />} loading={summarizing} onClick={handleSummarize} className="custom-font bg-purple-600 hover:bg-purple-700 text-white border-0">
                                خلاصه‌سازی با هوش مصنوعی
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'conversation',
                    label: (
                      <span className="custom-font inline-flex items-center gap-1.5">
                        <SoundOutlined />
                        مکالمه
                        {(isTranscribePending || isTranscribeProcessing) && <Spin size="small" />}
                        {isTranscribeFailed && <WarningOutlined className="text-red-500" />}
                      </span>
                    ),
                    children: (
                      <div className="space-y-4 min-w-0">
                  {/* بخش ۴: پخش صوت مکالمه */}
                  <DetailSection
                    icon={<SoundOutlined />}
                    title="فایل صوتی مکالمه"
                    description="پخش صدای ضبط‌شده تماس"
                    extra={
                      (call.audio_file || audioUrl) && (
                        <Space size={8}>
                          {call.audio_file && (
                            <Tag className="custom-font font-mono text-xs m-0" dir="ltr" color="cyan">
                              {call.audio_file}
                            </Tag>
                          )}
                          <Button
                            size="small"
                            type="text"
                            icon={<ReloadOutlined />}
                            loading={audioLoading}
                            onClick={() => loadAudio(id)}
                            className="custom-font text-xs text-gray-500 hover:text-teal-600"
                            title="بروزرسانی فایل صوتی"
                          >
                            بروزرسانی صوت
                          </Button>
                        </Space>
                      )
                    }
                  >
                    <div className="space-y-3">
                      {call.has_audio === false && !audioUrl && !call.audio_link ? (
                        <div className="text-gray-400 text-sm custom-font">
                          فایل صوتی برای این تماس ضبط نشده است.
                        </div>
                      ) : !audioUrl ? (
                        <Button
                          type="primary"
                          icon={<PlayCircleOutlined />}
                          loading={audioLoading}
                          onClick={handlePlayAudio}
                          className={`${PANEL_PRIMARY_BTN_CLASS} custom-font`}
                        >
                          دریافت و پخش فایل صوتی
                        </Button>
                      ) : (
                        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
                          <audio
                            key={`${id}-${audioUrl}`}
                            src={audioUrl}
                            controls
                            autoPlay
                            style={{ width: '100%' }}
                            className="custom-font"
                          >
                            مرورگر شما از پخش صوت پشتیبانی نمی‌کند.
                          </audio>
                        </div>
                      )}
                    </div>
                  </DetailSection>

                  {/* وضعیت رونویسی در حال انجام یا ناموفق */}
                  {(isTranscribePending || isTranscribeProcessing || isTranscribeFailed) && (
                    <DetailSection
                      icon={<AudioOutlined />}
                      color="amber"
                      title="رونویسی مکالمه"
                      description="تبدیل صدای تماس به متن"
                      extra={
                        <Button
                          type="link"
                          size="small"
                          icon={<ApiOutlined />}
                          onClick={() => navigate(`/call-center/calls/${id}/transcription-logs`)}
                          className="custom-font text-purple-600 hover:text-purple-700 font-medium"
                        >
                          لاگ‌های رونویسی
                        </Button>
                      }
                    >
                      {isTranscribePending || isTranscribeProcessing ? (
                        <div className="text-center py-6 custom-font space-y-3">
                          <Spin tip={<span className="custom-font">در حال رونویسی... (ممکن است چند دقیقه زمان ببرد)</span>} />
                          <div>
                            <Button
                              size="small"
                              type="dashed"
                              icon={<ApiOutlined />}
                              onClick={() => navigate(`/call-center/calls/${id}/transcription-logs`)}
                              className="custom-font text-xs text-purple-600 border-purple-300"
                            >
                              مشاهده زنده لاگ‌ها و وضعیت لحظه‌ای
                            </Button>
                          </div>
                        </div>
                      ) : isTranscribeFailed ? (
                        <Alert
                          type="error"
                          showIcon
                          className="custom-font"
                          message={<span className="custom-font">خطا در رونویسی مکالمه</span>}
                          description={
                            <div className="space-y-2">
                              <span className="custom-font block">
                                {call.transcribe_error || 'پردازش با خطا مواجه شد. می‌توانید مجدداً تلاش کنید.'}
                              </span>
                              <Button
                                size="small"
                                type="primary"
                                danger
                                icon={<ApiOutlined />}
                                onClick={() => navigate(`/call-center/calls/${id}/transcription-logs`)}
                                className="custom-font text-xs"
                              >
                                مشاهده لاگ‌های کامل و دلیل خطا در پایگاه‌داده
                              </Button>
                            </div>
                          }
                        />
                      ) : null}
                    </DetailSection>
                  )}

                  {/* بخش ۶: متن کامل مکالمه (آکاردئون، پیش‌فرض بسته) */}
                  {showTranscript && (
                    <CallTranscriptView
                      collapsible
                      segments={call.transcribed_segments}
                      transcript={call.transcript}
                      extra={(
                        <Button
                          type="link"
                          size="small"
                          icon={<ApiOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/call-center/calls/${id}/transcription-logs`);
                          }}
                          className="custom-font text-purple-600 hover:text-purple-700 text-xs"
                        >
                          لاگ‌های رونویسی
                        </Button>
                      )}
                    />
                  )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </Card>

          {/* مودال ویرایش/ثبت گزارش تماس */}
          <Modal
            title={
              <div className="custom-font font-bold flex items-center gap-2">
                <FileTextOutlined className="text-teal-600" />
                <span>ثبت و ویرایش گزارش تماس</span>
              </div>
            }
            open={reportModalOpen}
            onCancel={() => setReportModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setReportModalOpen(false)} className="custom-font">
                انصراف
              </Button>,
              <Button
                key="save"
                type="primary"
                className={PANEL_PRIMARY_BTN_CLASS}
                loading={savingReport}
                onClick={handleSaveReport}
                icon={<CheckCircleOutlined />}
              >
                ذخیره گزارش
              </Button>,
            ]}
            width={500}
            destroyOnClose
          >
            <div className="py-2" style={{ fontFamily: 'CustomFont' }}>
              <p className="text-xs text-gray-500 mb-2">
                گزارش و یادداشت این تماس جهت پیگیری‌های آینده و نمایش در پاپ‌آپ تماس‌های بعدی ذخیره می‌شود:
              </p>
              <Input.TextArea
                rows={5}
                placeholder="شرح مکالمه، توافقات یا نکات مهم را اینجا وارد کنید..."
                value={editingReportText}
                onChange={(e) => setEditingReportText(e.target.value)}
                className="custom-font text-sm rounded-xl"
                showCount
                maxLength={1000}
              />
            </div>
          </Modal>
        </div>
      </ConfigProvider>
    </MainLayout>
  );
};

export default ViewCallCenterCall;
